import React, { useEffect, useRef, useCallback } from "react";

const Section = ({ children, className = "" }) => (
  <section className={`w-full max-w-2xl mx-auto ${className} animate-fadeUp`}>{children}</section>
);

function Step({ index, title, text }) {
  return (
    <div className="flex items-start gap-4 bg-gray-50 p-3 sm:p-4 rounded-lg">
      <div className="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-sm">
        {index}
      </div>
      <div className="flex-1">
        <h3 className="font-bold text-gray-800 text-sm sm:text-base">{title}</h3>
        <p className="text-gray-600 text-xs sm:text-sm mt-1">{text}</p>
      </div>
    </div>
  );
}

function ProblemCard({ icon, title, description }) {
  return (
    <div className="border-2 border-blue-200 rounded-lg p-3 sm:p-4 bg-blue-50">
      <div className="flex items-start gap-2 mb-2">
        <span className="text-lg sm:text-xl">{icon}</span>
        <h3 className="font-bold text-blue-700 text-xs sm:text-sm">{title}</h3>
      </div>
      <p className="text-xs sm:text-sm text-gray-700 leading-relaxed">{description}</p>
    </div>
  );
}

export default function InvestorWelcome({
  onDismiss,
  onNavigateToDemoPage,
  logoPath = "/logo.png",
}) {
  const overlayRef = useRef(null);
  const closeBtnRef = useRef(null);

  const handleClose = useCallback(() => {
    onDismiss?.();
  }, [onDismiss]);

  const handleOverlayClick = useCallback((e) => {
    if (e.target === overlayRef.current) {
      onDismiss?.();
    }
  }, [onDismiss]);

  const handleStartDemo = useCallback(() => {
    onDismiss?.();
    onNavigateToDemoPage?.();
  }, [onDismiss, onNavigateToDemoPage]);

  useEffect(() => {
    const overlay = overlayRef.current;
    if (!overlay) return;
    const focusable = overlay.querySelectorAll(
      'a[href], button, textarea, input, select, [tabindex]:not([tabindex="-1"])'
    );
    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    function onKey(e) {
      if (e.key === "Escape") {
        handleClose();
      }
      if (e.key === "Tab" && focusable.length > 1) {
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }

    document.addEventListener("keydown", onKey);
    closeBtnRef.current?.focus();
    return () => document.removeEventListener("keydown", onKey);
  }, [handleClose]);

  return (
    <div
      ref={overlayRef}
      onClick={handleOverlayClick}
      className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-2 sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-label="RepMingle real time service monetization platform"
    >
      <div className="bg-white rounded-xl sm:rounded-2xl shadow-2xl w-full max-w-5xl max-h-[95vh] overflow-y-auto animate-zoomIn pointer-events-auto">
        {/* ===== HEADER ===== */}
        <div className="bg-gradient-to-r from-blue-600 via-blue-500 to-purple-600 text-white p-3 sm:p-6 md:p-8 relative">
          <button
            ref={closeBtnRef}
            onClick={handleClose}
            type="button"
            aria-label="Close presentation"
            className="absolute top-3 right-3 sm:top-4 sm:right-4 p-2 hover:bg-white/20 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-white/60 z-20 cursor-pointer"
          >
            <span className="text-xl font-bold">×</span>
          </button>
          <div className="flex items-start gap-2 sm:gap-4 mb-2 sm:mb-3 pr-10">
            <div className="flex-shrink-0 w-10 h-10 sm:w-14 sm:h-14 md:w-16 md:h-16 flex items-center justify-center overflow-hidden bg-white/10 rounded-lg">
              <img
                src={logoPath}
                alt="RepMingle Logo"
                className="w-full h-full object-contain"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="text-xl sm:text-2xl md:text-4xl font-black leading-tight break-words">
                RepMingle
              </h1>
              <p className="text-xs sm:text-sm md:text-base text-blue-100 mt-1">
                Real Time Service Monetization Platform
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 mb-4 sm:mb-6">
            <div className="flex items-center gap-2 bg-white/15 backdrop-blur-sm border border-amber-300/50 rounded-lg px-2 sm:px-3 py-1.5 sm:py-2 max-w-fit text-xs sm:text-sm">
              <div className="w-1 h-5 sm:h-6 bg-gradient-to-b from-amber-300 to-orange-400 rounded-full"></div>
              <div>
                <p className="font-bold text-white text-xs sm:text-sm">PATENT PENDING</p>
                <p className="text-xs text-blue-100">US 2025</p>
              </div>
            </div>
          </div>
          <div className="mt-3 sm:mt-6">
            <h2 className="text-base sm:text-xl md:text-2xl font-bold text-blue-100">
              Match members with services instantly
            </h2>
            <p className="text-xs sm:text-sm md:text-base text-blue-100/90 mt-1 sm:mt-2 leading-relaxed">
              Real time discovery. Smart matching. Instant payment. Unlock on site impulse service revenue.
            </p>
          </div>
        </div>

        {/* ==== DEMO CTA AT THE TOP ==== */}
        <Section className="bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg sm:rounded-xl p-4 sm:p-6 md:p-8 mt-4">
          <h2 className="text-base sm:text-2xl md:text-3xl font-bold mb-2">
            See the system in action
          </h2>
          <p className="text-xs sm:text-base md:text-lg text-blue-100 mb-4">
            Interactive demo. Watch kiosk booking in seconds.
          </p>
          <button
            onClick={handleStartDemo}
            type="button"
            className="w-full bg-white text-blue-600 font-bold py-2.5 sm:py-3 md:py-4 px-4 rounded-lg hover:bg-blue-50 transition-colors text-xs sm:text-base md:text-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-600 cursor-pointer"
          >
            Launch interactive demo →
          </button>
        </Section>

        {/* ===== CONTENT ===== */}
        <div className="p-3 sm:p-6 md:p-8 space-y-4 sm:space-y-6 md:space-y-8">
          {/* EXECUTIVE SUMMARY */}
          <Section className="bg-gradient-to-r from-blue-50 to-blue-100 border-2 border-blue-300 rounded-lg sm:rounded-xl p-4 sm:p-6 md:p-8">
            <h2 className="text-lg sm:text-2xl md:text-3xl font-bold text-gray-800 mb-3 sm:mb-4">
              Executive Summary
            </h2>
            <div className="bg-white rounded-lg p-3 sm:p-4 border-l-4 border-blue-600">
              <p className="text-xs sm:text-sm md:text-base text-gray-700 leading-relaxed">
                <strong className="text-blue-700">RepMingle</strong> RepMingle converts idle service capacity into instant revenue by enabling micro-session sales that intelligently connect gym members with available services at their moment of need. RepMingle proprietary matching system considers facility capacity, member context, and service availability to surface the right offer at the right time. RepMingle generates recurring platform fees and transaction-based revenue as facilities unlock previously wasted capacity.
              </p>
            </div>
          </Section>

          {/* THE PROBLEM */}
          <Section>
            <h2 className="text-lg sm:text-2xl font-bold text-gray-800 mb-3 sm:mb-4">
              Breaking Down the Hidden Barriers for Operators and Members
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <ProblemCard
                icon="⏱️"
                title="Members cannot access instant guidance"
                description="Short expert interventions change outcomes, yet require advance booking. No system for an impulse moment on the floor."
              />
              <ProblemCard
                icon="📉"
                title="Cancellations create income volatility"
                description="Empty minutes hurt trainer income and facility revenue. Fast fill is missing."
              />
              <ProblemCard
                icon="💰"
                title="Acquisition costs drain earnings"
                description="Trainers lack built in discovery at the facility. Marketing spend is wasteful."
              />
              <ProblemCard
                icon="🏆"
                title="Credentials stay invisible"
                description="Skills and certifications are not surfaced at the right time."
              />
              <ProblemCard
                icon="🔄"
                title="Switching friction blocks discovery"
                description="Members locked in packages avoid trying a new expert when a quick check could help."
              />
            </div>
          </Section>

          {/* HOW IT WORKS */}
          <Section>
            <h2 className="text-lg sm:text-2xl font-bold text-gray-800 mb-3 sm:mb-4">
              How RepMingle works. 30 second flow
            </h2>
            <div className="space-y-2 sm:space-y-4 mb-6">
              <Step index={1} title="Member sees a prompt" text="Smart nudge. For example Fix your squat. Expert available now. 45 dollars." />
              <Step index={2} title="Zero friction decision" text="Outcome first. Fast ID or quick join in 30 seconds." />
              <Step index={3} title="Instant payment" text="Charge confirmed at checkout." />
              <Step index={4} title="Smart match" text="7 second assignment using skill, availability, performance." />
              <Step index={5} title="Session starts" text="Trainer notified. Location and member details ready." />
            </div>
            <div className="w-full">
              <img
                src="/RepMingleKiosk.png"
                alt="RepMingle kiosk with real time service matching"
                className="w-full h-auto rounded-lg shadow-lg border border-gray-300"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
              <p className="text-center text-xs sm:text-sm text-gray-600 mt-3">
                RepMingle kiosk. Instant prompt. Fast checkout.
              </p>
            </div>
          </Section>

          {/* ==== BEGIN NEW REVENUE SECTION HERE ==== */}
          <Section>
            <h2 className="text-lg sm:text-2xl font-bold text-gray-800 mb-3 sm:mb-4">
              Revenue Strategy: Performance Revenue Share & Vertical SaaS
            </h2>
            <div className="space-y-3 mb-6">
              <div className="bg-blue-50 border-2 border-blue-300 rounded-lg p-4">
                <h3 className="font-bold text-blue-700 mb-2 text-sm">💸 Transaction Breakdown</h3>
                <p className="text-xs sm:text-sm md:text-base text-gray-700">
                  Example: Each instant coaching or service session is <b>$45</b>.<br />
                  - <b>Stripe/card fee:</b> $1.61<br />
                  - <b>Trainer payout:</b> $35.00<br />
                  - <b>Facility share:</b> $4.20<br />
                  - <b>RepMingle platform share:</b> $4.19
                </p>
              </div>
              <div className="bg-green-50 border-2 border-green-300 rounded-lg p-4">
                <h3 className="font-bold text-green-700 mb-2 text-sm">📊 Scalable Growth Model</h3>
                <p className="text-xs sm:text-sm md:text-base text-gray-700">
                  Example: With 100 sites, 6 paid sessions/day/site<br />
                  → <b>Monthly platform revenue:</b> $75,420<br />
                  → <b>Facility (all sites):</b> $75,600/month<br />
                  → At 200 sites: Nearly $151,000/month platform revenue
                </p>
                
              </div>
              <div className="bg-yellow-50 border-2 border-yellow-300 rounded-lg p-4">
                <h3 className="font-bold text-yellow-700 mb-2 text-sm">⚡ Operator Model</h3>
                <p className="text-xs sm:text-sm md:text-base text-gray-700">
                  • No upfront cost for operators (the hardware kiosk can be leased, sold, or bundled depending on the deal)<br />
                  • Monthly SaaS fee: $500–$2,000/site (optionally bundled at scale)<br />
                  • Enterprise/chain licensing or white-label deployments are supported
                </p>
              </div>
              <div className="bg-purple-50 border-2 border-purple-300 rounded-lg p-4">
                <h3 className="font-bold text-purple-700 mb-2 text-sm">🏅 Value Creation</h3>
                <p className="text-xs sm:text-sm md:text-base text-gray-700">
                  • Facilities earn from untapped, “empty minute” revenue<br />
                  • Trainers maximize earnings via impulse PT<br />
                  • RepMingle scales recurring SaaS and per-session transaction fees as adoption grows
                </p>
              </div>
              <div className="bg-white border-2 border-gray-300 rounded-lg p-4">
                <p className="text-xs sm:text-sm md:text-base text-gray-800 font-semibold">
                  <b>Gross margin:</b> 60–66% after fees, cloud, and support.<br />
                  <b>Customer payback:</b> 8–12 weeks.
                </p>
              </div>
            </div>
          </Section>
          <Section className="bg-gray-50 border-2 border-gray-300 rounded-lg p-4 sm:p-6">
  <h3 className="font-bold text-gray-800 mb-2 sm:mb-3 text-sm sm:text-base">
    📊 Market & Retention Data (2025, U.S.)
  </h3>
  <div className="text-xs sm:text-sm text-gray-700 space-y-1.5">
    <p><strong>Industry size:</strong> $45–46B/year (MMCG, IBISWorld)</p>
    <p><strong>Total facilities:</strong> Over 41,000 facilities recovered to pre-pandemic levels (MMCG, IHRSA, HFA)</p>
    <p><strong>Franchise locations:</strong> The U.S. fitness industry has 87 franchisors and over 20,000 franchise locations</p>
    
    <p><strong>Personal training revenue:</strong> ~$12B, growing 8.2% annually (IBISWorld)</p>
    <p><strong>Member retention rate:</strong> 66–75% boutique, 50–60% traditional gyms (HFA, SmartHealthClubs)</p>
    <p><strong>Total members:</strong> 77M Americans (HFA, MMCG)</p>
  </div>
</Section>

          {/* ==== END NEW REVENUE SECTION ==== */}

          {/* ...rest of your old content such as market data, contact etc. */}

          {/* ==== REPEAT DEMO CTA AT THE END ==== */}
          <Section className="bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg p-4 sm:p-6 md:p-8">
            <h2 className="text-base sm:text-2xl md:text-3xl font-bold mb-2">
              See the system in action
            </h2>
            <p className="text-xs sm:text-base md:text-lg text-blue-100 mb-4">
              Interactive demo. Watch kiosk booking in seconds.
            </p>
            <button
              onClick={handleStartDemo}
              type="button"
              className="w-full bg-white text-blue-600 font-bold py-2.5 sm:py-3 md:py-4 px-4 rounded-lg hover:bg-blue-50 transition-colors text-xs sm:text-base md:text-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-600 cursor-pointer"
            >
              Launch interactive demo →
            </button>
          </Section>
        </div>
        {/* Footer */}
        <div className="bg-gray-50 px-3 sm:px-6 py-3 sm:py-4 text-center text-xs sm:text-sm text-gray-600 border-t border-gray-200">
          <p>© 2025 RepMingle. Patent pending US 2025.</p>
        </div>
      </div>
      <style>{`
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(24px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes zoomIn {
          from { opacity: 0; transform: scale(0.98); }
          to { opacity: 1; transform: scale(1); }
        }
        .animate-fadeUp { animation: fadeUp 400ms ease-out both; }
        .animate-zoomIn { animation: zoomIn 250ms ease-out both; }
      `}</style>
    </div>
  );
}
